import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  ChannelBadge,
  type ChannelKind,
  ConfirmAction,
  EmptyState,
  Input,
  Label,
  ListPage,
  RefreshButton,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  StatusBadge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@skylabs-digital/backoffice-kit';
import { useApiClient, useSnackbar } from '@skylabs-digital/react-proto-kit';
import { RotateCw, Search } from 'lucide-react';
import { useState } from 'react';
import { failedNotificationsApi } from '../api/failedNotifications.api';
import { OutboxFailedSection } from '../components/dlq/OutboxFailedSection';
import { CHANNEL_OPTIONS } from '../constants/channel-colors';
import { ERROR_CATEGORY_INFO, ERROR_CATEGORY_OPTIONS } from '../constants/error-categories';
import type { FailedNotification } from '../schemas/failedNotification.schema';
import { statusTone } from '../utils/status-tone';

interface DlqResultsProps {
  query: Record<string, string>;
}

/**
 * Translate the list filters into the retry-all body.
 *
 * `limit` is deliberately dropped: on the listing it means "page size", and
 * sending it as the retry bound would silently requeue only the first page of a
 * result set the operator believes they are retrying whole.
 */
export function buildRetryAllBody(query: Record<string, string>): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  if (query.channel) body.channel = query.channel;
  if (query.eventType) body.eventType = query.eventType;
  if (query.since) body.since = toIsoDateTime(query.since);
  // RFC 0002 Fase 2, decision 8: con categoria, "Retry all" reintenta SOLO
  // esa categoria; sin ella el backend excluye por defecto
  // recipient|bounce|complaint|config, que no cambian con un reintento.
  if (query.errorCategory) body.errorCategory = query.errorCategory;
  return body;
}

/** Categorias que `POST /notifications/failed/retry-all` salta cuando no se pide una explicita. */
export const RETRY_ALL_DEFAULT_EXCLUDED = ['recipient', 'bounce', 'complaint', 'config'] as const;

export interface RetryAllResult {
  requeued: number;
  skipped: Record<string, number>;
  notificationIds: string[];
}

/** "Requeued 15 · skipped: config 2, recipient 1" — la respuesta nueva, legible en un snackbar. */
export function formatRetryAllResult(result: RetryAllResult): string {
  const skipped = Object.entries(result.skipped)
    .filter(([, n]) => n > 0)
    .map(([category, n]) => `${category} ${n}`)
    .join(', ');
  return skipped
    ? `Requeued ${result.requeued} · skipped: ${skipped}`
    : `Requeued ${result.requeued}`;
}

/**
 * `<input type="date">` yields `YYYY-MM-DD`; the API declares `since` as an ISO
 * datetime and rejects the bare date with a 400. Widen it to the start of that
 * day so "since the 3rd" means the whole 3rd.
 */
export function toIsoDateTime(value: string): string {
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return `${value}T00:00:00.000Z`;
  return value;
}

/** Search filters for `GET /notifications/failed`, before pagination. */
export interface DlqSearchFilters {
  channel: string;
  eventType: string;
  since: string;
  limit: string;
  errorCategory: string;
}

/** Pulled out of the search handler so the querystring it builds — including
 * the RFC 0002 `errorCategory` filter — can be pinned in a test without
 * having to drive Radix's `Select` through jsdom. */
export function buildDlqSearchQuery(filters: DlqSearchFilters): Record<string, string> {
  const query: Record<string, string> = {};
  if (filters.channel) query.channel = filters.channel;
  if (filters.eventType) query.eventType = filters.eventType;
  if (filters.since) query.since = toIsoDateTime(filters.since);
  if (filters.limit) query.limit = filters.limit;
  if (filters.errorCategory) query.errorCategory = filters.errorCategory;
  return query;
}

function DlqResults({ query }: DlqResultsProps) {
  const { connector } = useApiClient();
  const { showSnackbar } = useSnackbar();
  const { data, loading, error, refetch } = failedNotificationsApi.withQuery(query).useList();
  const [retrying, setRetrying] = useState<string | null>(null);

  const notifications: FailedNotification[] = data ?? [];

  const handleRetry = async (id: string) => {
    setRetrying(id);
    try {
      await connector.post(`notifications/${id}/retry`, {});
      showSnackbar({ message: `Retry queued for ${id.slice(0, 8)}…`, variant: 'success' });
      refetch();
    } catch {
      showSnackbar({ message: 'Failed to retry notification', variant: 'error' });
    } finally {
      setRetrying(null);
    }
  };

  const handleRetryAll = async () => {
    // Send the filters that produced the list on screen. Posting `{}` asked
    // the API to requeue EVERY failure for the app while the dialog promised
    // "every match in the current filter" — the two disagreed, and the
    // dangerous reading was the one that ran (noten-xeh).
    //
    // El connector de RPK 3 no tira en un no-2xx: resuelve un ErrorResponse.
    const res = await connector.post<RetryAllResult>(
      'notifications/failed/retry-all',
      buildRetryAllBody(query),
    );
    if (res.success) {
      showSnackbar({ message: formatRetryAllResult(res.data), variant: 'success' });
      refetch();
    } else {
      showSnackbar({ message: res.message ?? 'Failed to retry all', variant: 'error' });
    }
  };

  if (error) {
    return (
      <EmptyState
        title="Couldn't load failed notifications"
        description={error instanceof Error ? error.message : 'Try again in a moment.'}
        action={<Button onClick={() => refetch()}>Retry</Button>}
        className="border-destructive/40 bg-destructive/5"
      />
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <div>
          <CardTitle className="text-base">
            {notifications.length} failed notification{notifications.length !== 1 ? 's' : ''}
          </CardTitle>
          <CardDescription>Retry individually or in bulk.</CardDescription>
        </div>
        <div className="flex items-center gap-2">
          <RefreshButton size="sm" onRefresh={refetch} isRefreshing={loading} />
          {notifications.length > 0 ? (
            <ConfirmAction
              trigger={
                <Button variant="destructive" size="sm">
                  <RotateCw /> Retry all
                </Button>
              }
              title={`Retry all ${notifications.length} failed notifications?`}
              description={
                query.errorCategory
                  ? `This will requeue every "${query.errorCategory}" failure in the current filter. Use carefully on large result sets.`
                  : `This will requeue every match in the current filter, excluding ${RETRY_ALL_DEFAULT_EXCLUDED.join(', ')} failures (they do not change with a retry). Use carefully on large result sets.`
              }
              confirmLabel="Retry all"
              destructive
              onConfirm={handleRetryAll}
            />
          ) : null}
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>ID</TableHead>
              <TableHead>To</TableHead>
              <TableHead>Channel</TableHead>
              <TableHead>Event type</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Category</TableHead>
              <TableHead className="text-right">Attempts</TableHead>
              <TableHead className="text-right">Retry round</TableHead>
              <TableHead>Created</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              Array.from({ length: 3 }).map((_, i) => (
                <TableRow key={`s-${i}`}>
                  {Array.from({ length: 10 }).map((__, j) => (
                    <TableCell key={`s-${i}-${j}`}>
                      <Skeleton className="h-4 w-16" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : notifications.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} className="py-10 text-center text-sm text-muted-foreground">
                  No failed notifications match this filter.
                </TableCell>
              </TableRow>
            ) : (
              notifications.map((n) => (
                <TableRow key={n.id}>
                  <TableCell className="font-mono text-xs">{n.id.slice(0, 8)}…</TableCell>
                  <TableCell className="max-w-[200px] truncate text-sm">{n.to}</TableCell>
                  <TableCell>
                    <ChannelBadge channel={n.channel as ChannelKind} />
                  </TableCell>
                  <TableCell className="font-mono text-xs">{n.eventType}</TableCell>
                  <TableCell>
                    <StatusBadge tone={statusTone(n.status, 'danger')}>{n.status}</StatusBadge>
                  </TableCell>
                  <TableCell>
                    {n.lastErrorCategory || n.lastErrorCode ? (
                      <div className="flex flex-wrap items-center gap-1.5">
                        {n.lastErrorCategory ? (
                          <StatusBadge
                            tone={ERROR_CATEGORY_INFO[n.lastErrorCategory].tone}
                            title={ERROR_CATEGORY_INFO[n.lastErrorCategory].description}
                          >
                            {ERROR_CATEGORY_INFO[n.lastErrorCategory].label}
                          </StatusBadge>
                        ) : null}
                        {n.lastErrorCode ? (
                          <span className="font-mono text-xs text-muted-foreground">
                            {n.lastErrorCode}
                          </span>
                        ) : null}
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{n.attemptsCount}</TableCell>
                  <TableCell className="text-right tabular-nums">{n.retryRound}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {new Date(n.createdAt).toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={retrying === n.id}
                      onClick={() => handleRetry(n.id)}
                    >
                      <RotateCw /> {retrying === n.id ? 'Retrying…' : 'Retry'}
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

export function DlqPage() {
  const [channel, setChannel] = useState('');
  const [eventType, setEventType] = useState('');
  const [since, setSince] = useState('');
  const [limit, setLimit] = useState('50');
  const [errorCategory, setErrorCategory] = useState('');
  const [activeQuery, setActiveQuery] = useState<Record<string, string> | null>(null);

  const handleSearch = () => {
    setActiveQuery(buildDlqSearchQuery({ channel, eventType, since, limit, errorCategory }));
  };

  return (
    <ListPage
      title="DLQ Inspector"
      description="Inspect and re-queue failed notifications that hit the dead letter queue."
    >
      <Card>
        <CardContent className="pt-6">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            <div className="space-y-2">
              <Label htmlFor="dlq-channel">Channel</Label>
              <Select
                value={channel || 'all'}
                onValueChange={(v) => setChannel(v === 'all' ? '' : v)}
              >
                <SelectTrigger id="dlq-channel">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {CHANNEL_OPTIONS.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2 lg:col-span-2">
              <Label htmlFor="dlq-event">Event type</Label>
              <Input
                id="dlq-event"
                value={eventType}
                onChange={(e) => setEventType(e.target.value)}
                placeholder="user.welcome"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="dlq-since">Since</Label>
              <Input
                id="dlq-since"
                type="date"
                value={since}
                onChange={(e) => setSince(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="dlq-limit">Limit</Label>
              <Input
                id="dlq-limit"
                type="number"
                min={1}
                max={500}
                value={limit}
                onChange={(e) => setLimit(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="dlq-error-category">Category</Label>
              <Select
                value={errorCategory || 'all'}
                onValueChange={(v) => setErrorCategory(v === 'all' ? '' : v)}
              >
                <SelectTrigger id="dlq-error-category">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {ERROR_CATEGORY_OPTIONS.map((c) => (
                    <SelectItem key={c} value={c}>
                      {ERROR_CATEGORY_INFO[c].label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="mt-4 flex justify-end">
            <Button onClick={handleSearch}>
              <Search /> Search
            </Button>
          </div>
        </CardContent>
      </Card>

      {activeQuery ? <DlqResults query={activeQuery} /> : null}

      <OutboxFailedSection />
    </ListPage>
  );
}
